import { approach, clamp, damp, lerp, smoothstep } from '../core/math';
import type { AxisSource } from './input';

/**
 * Driving input shaping (docs/DRIVING.md §8): the raw steer axis of each device becomes the steer the
 * vehicle model sees. Pure and time-stepped, so it is unit-tested (driveShaping.test.ts) and the
 * physics (which the online server validates) is untouched: only what the driver asks for changes.
 *
 *  - keys are digital, so A/D ramp up over a time that grows with speed (a tap at 60 mph is a nudge,
 *    a hold is still full lock) and snap back to centre;
 *  - a pad stick gets a response curve (finer near the centre, full at the rim);
 *  - the touch stick is already shaped by TouchDrive (src/input/touchDrive.ts) and is
 *    only smoothed a little against the thumb's 60 Hz jitter;
 *  - virtual input (tests, __bh.setInput) passes straight through, so scripted playtests stay exact.
 */

export interface DriveShapeOptions {
  /** 0.5..1.5: how quickly the wheel answers (keys ramp faster, the pad curve straightens). */
  sensitivity: number;
  /** Driving assists on: the keys' ramp grows with speed. Off: the keys are a plain quick ramp. */
  assists: boolean;
}

export const DEFAULT_DRIVE_SHAPE: DriveShapeOptions = { sensitivity: 1, assists: true };

/** Keys: seconds from centre to full lock at a standstill, and at speed (ramped between 6 and 28 m/s). */
export const KEY_RAMP_SLOW = 0.05;
export const KEY_RAMP_FAST = 0.42;
/** Keys: seconds from full lock back to centre (or across to the other side). */
export const KEY_RETURN = 0.07;
/** Pad stick response exponent at sensitivity 1 (1 = linear). */
export const PAD_CURVE = 1.6;
/** Touch: smoothing rate (1/s) on the stick's steer. */
const TOUCH_SMOOTH = 28;

/** Pad stick -> steer: |x|^curve keeps the sign; sensitivity straightens (>1) or bends (<1) the curve. */
export function padSteerCurve(x: number, sensitivity = 1): number {
  const e = 1 + (PAD_CURVE - 1) / Math.max(0.25, sensitivity);
  return Math.sign(x) * Math.pow(Math.min(1, Math.abs(x)), e);
}

/** Seconds the keys take from centre to full lock at `speed` (m/s). */
export function keyRampSeconds(speed: number, o: DriveShapeOptions = DEFAULT_DRIVE_SHAPE): number {
  const t = o.assists ? lerp(KEY_RAMP_SLOW, KEY_RAMP_FAST, smoothstep(6, 28, Math.abs(speed))) : KEY_RAMP_SLOW;
  return t / Math.max(0.25, o.sensitivity);
}

export class DriveShaper {
  private keySteer = 0;
  private touchSteer = 0;

  /** Raw steer axis (-1..1) from `source` -> the steer to hand the vehicle. `speed` in m/s, `dt` seconds. */
  steer(raw: number, source: AxisSource, speed: number, dt: number, o: DriveShapeOptions = DEFAULT_DRIVE_SHAPE): number {
    const target = clamp(raw, -1, 1);
    switch (source) {
      case 'keyboard': {
        const crossing = target !== 0 && this.keySteer !== 0 && Math.sign(target) !== Math.sign(this.keySteer);
        const winding = target !== 0 && !crossing && Math.abs(target) > Math.abs(this.keySteer);
        // Crossing sides snaps to the centre first, then ramps like a fresh press.
        this.keySteer = winding ? approach(this.keySteer, target, dt / keyRampSeconds(speed, o)) : approach(this.keySteer, crossing ? 0 : target, dt / KEY_RETURN);
        this.touchSteer = this.keySteer;
        return this.keySteer;
      }
      case 'gamepad': {
        const s = padSteerCurve(target, o.sensitivity);
        this.keySteer = s;
        this.touchSteer = s;
        return s;
      }
      case 'touch': {
        this.touchSteer += (target - this.touchSteer) * damp(TOUCH_SMOOTH, dt);
        if (Math.abs(this.touchSteer) < 1e-4) this.touchSteer = 0;
        this.keySteer = this.touchSteer;
        return this.touchSteer;
      }
      case 'virtual':
        this.keySteer = target;
        this.touchSteer = target;
        return target;
      default:
        // Hands off: the keys' memory returns to centre, so a tap always starts from zero.
        this.keySteer = approach(this.keySteer, 0, dt / KEY_RETURN);
        this.touchSteer = this.keySteer;
        return this.keySteer;
    }
  }

  reset(): void {
    this.keySteer = 0;
    this.touchSteer = 0;
  }
}
